// GA4 e-commerce tracking via the Google Tag Manager dataLayer.
// Events follow the GA4 recommended e-commerce schema:
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
// GTM must be installed (snippet in index.html) and have GA4 event tags
// configured with "Send Ecommerce data" for these to reach GA4.

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export interface GtmItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  price?: number; // final unit price (after discount), EUR
  discount?: number; // absolute discount per unit, EUR
  quantity?: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Final unit price after a percentage discount. */
export const discountedPrice = (price: number, discountPercentage?: number | null): number =>
  discountPercentage && discountPercentage > 0
    ? round2(price * (1 - discountPercentage / 100))
    : round2(price);

const pushEvent = (event: string, ecommerce: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Clear the previous ecommerce object so GTM doesn't merge stale item data.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
};

const itemsValue = (items: GtmItem[]): number =>
  round2(items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0));

export const trackViewItem = (item: GtmItem) => {
  pushEvent('view_item', {
    currency: 'EUR',
    value: round2((item.price || 0) * (item.quantity || 1)),
    items: [{ ...item, quantity: item.quantity ?? 1 }],
  });
};

export const trackAddToCart = (item: GtmItem) => {
  pushEvent('add_to_cart', {
    currency: 'EUR',
    value: round2((item.price || 0) * (item.quantity || 1)),
    items: [{ ...item, quantity: item.quantity ?? 1 }],
  });
};

export const trackBeginCheckout = (items: GtmItem[], value?: number) => {
  if (!items.length) return;
  pushEvent('begin_checkout', {
    currency: 'EUR',
    value: value !== undefined ? round2(value) : itemsValue(items),
    items,
  });
};

/**
 * Fires `purchase` exactly once per order id — the success page can be
 * refreshed or re-rendered, and GA4 treats repeated transaction_ids
 * inconsistently, so we dedupe client-side via sessionStorage.
 */
export const trackPurchase = (
  transactionId: string,
  value: number,
  items: GtmItem[],
  shipping?: number,
) => {
  if (!transactionId) return;
  const dedupeKey = `ga4_purchase_${transactionId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
  } catch {
    // sessionStorage unavailable (private mode edge cases) — still track.
  }
  pushEvent('purchase', {
    transaction_id: transactionId,
    currency: 'EUR',
    value: round2(value),
    ...(shipping !== undefined ? { shipping: round2(shipping) } : {}),
    items,
  });
};
